import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdmin } from '../../context/AdminContext';
import { 
  CreditCard, 
  Plus, 
  Edit, 
  Trash2, 
  DollarSign,
  Calendar,
  Check,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

const PlanManagement = () => {
  const { t } = useTranslation();
  const { 
    plans, 
    loading, 
    fetchPlans, 
    updatePlan,
    createPlan,
    deletePlan 
  } = useAdmin();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const loadPlans = useCallback(() => {
    return fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleCreatePlan = (planData) => {
    setSelectedPlan(null);
    setShowCreateModal(true);
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    setShowEditModal(true);
  };

  const handleDeletePlan = (plan) => {
    setSelectedPlan(plan);
    setShowDeleteModal(true);
  };

  const handleSavePlan = async (planData) => {
    const result = await createPlan(planData);
    if (result.success) {
  toast.success(t('planManagement.messages.created'));
      setShowCreateModal(false);
  loadPlans();
    } else {
      toast.error(result.error);
    }
  };

  const handleUpdatePlan = async (planData) => {
    const result = await updatePlan(selectedPlan.id, planData);
    if (result.success) {
  toast.success(t('planManagement.messages.updated'));
      setShowEditModal(false);
      setSelectedPlan(null);
  loadPlans();
    } else {
      toast.error(result.error);
    }
  };

  const handleConfirmDelete = async () => {
    const result = await deletePlan(selectedPlan.id);
    if (result.success) {
  toast.success(t('planManagement.messages.deleted'));
      setShowDeleteModal(false);
      setSelectedPlan(null);
  loadPlans();
    } else {
      toast.error(result.error);
    }
  };

  const formatPrice = (priceInCents) => {
    return `$${(priceInCents / 100).toFixed(2)}`;
  };

  const getBillingIntervalText = (interval) => {
    switch (interval) {
      case 'MONTHLY': return t('planManagement.billingInterval.monthly');
      case 'YEARLY': return t('planManagement.billingInterval.yearly');
      default: return interval;
    }
  };

  if (loading && !plans.length) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">{t('planManagement.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">{t('planManagement.title')}</h1>
            <p className="text-gray-400">{t('planManagement.subtitle')}</p>
          </div>
          <button
            onClick={handleCreatePlan}
            className="btn-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('planManagement.actions.create')}
          </button>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.isArray(plans) && plans.map((plan) => (
            <div key={plan.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center mr-3">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{plan.name}</h3>
                    <p className="text-gray-400 text-sm">{plan.code}</p>
                  </div>
                </div>
                <div className="flex space-x-1">
                  {plan.popular && (
                    <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
                      {t('planManagement.popularBadge')}
                    </span>
                  )}
                  {plan.recommended && (
                    <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">
                      {t('planManagement.recommendedBadge')}
                    </span>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-baseline mb-2">
                  <span className="text-3xl font-bold text-white">
                    {formatPrice(plan.priceAmount)}
                  </span>
                  <span className="text-gray-400 ml-2">
                    /{getBillingIntervalText(plan.billingInterval).toLowerCase()}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">{plan.description}</p>
              </div>

              <div className="space-y-2 mb-6">
                <div className="flex items-center text-gray-400 text-sm">
                  <Calendar className="w-4 h-4 mr-2" />
                  <span>{getBillingIntervalText(plan.billingInterval)} {t('planManagement.labels.billing')}</span>
                </div>
                <div className="flex items-center text-gray-400 text-sm">
                  <DollarSign className="w-4 h-4 mr-2" />
                  <span>{t('planManagement.labels.currency')}: {plan.currency}</span>
                </div>
                <div className="flex items-center text-gray-400 text-sm">
                  {plan.active ? (
                    <Check className="w-4 h-4 mr-2 text-green-400" />
                  ) : (
                    <X className="w-4 h-4 mr-2 text-red-400" />
                  )}
                  <span className={plan.active ? 'text-green-400' : 'text-red-400'}>
                    {plan.active ? t('planManagement.status.active') : t('planManagement.status.inactive')}
                  </span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => handleEditPlan(plan)}
                  className="flex-1 btn-secondary text-sm"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  {t('planManagement.actions.edit')}
                </button>
                <button
                  onClick={() => handleDeletePlan(plan)}
                  className="flex-1 btn-danger text-sm"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t('planManagement.actions.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Create Plan Modal */}
        {showCreateModal && (
          <CreatePlanModal
            onSave={handleSavePlan}
            onClose={() => setShowCreateModal(false)}
          />
        )}

        {/* Edit Plan Modal */}
        {showEditModal && selectedPlan && (
          <EditPlanModal
            plan={selectedPlan}
            onSave={handleUpdatePlan}
            onClose={() => {
              setShowEditModal(false);
              setSelectedPlan(null);
            }}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedPlan && (
          <DeletePlanModal
            plan={selectedPlan}
            onConfirm={handleConfirmDelete}
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedPlan(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Create Plan Modal Component
const CreatePlanModal = ({ onSave, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    priceAmount: '',
    currency: 'USD',
    billingInterval: 'MONTHLY',
    active: true,
    popular: false,
    recommended: false,
    displayOrder: 0
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const planData = {
      ...formData,
      priceAmount: parseInt(formData.priceAmount) * 100 // Convert to cents
    };
    onSave(planData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-white mb-4">{t('planManagement.createModal.title')}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.name')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="input-field w-full"
              required
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.code')}</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
              className="input-field w-full"
              required
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="input-field w-full"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.price')}</label>
            <input
              type="number"
              step="0.01"
              value={formData.priceAmount}
              onChange={(e) => setFormData({...formData, priceAmount: e.target.value})}
              className="input-field w-full"
              required
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.billingInterval')}</label>
            <select
              value={formData.billingInterval}
              onChange={(e) => setFormData({...formData, billingInterval: e.target.value})}
              className="input-field w-full"
            >
              <option value="MONTHLY">{t('planManagement.billingInterval.monthly')}</option>
              <option value="YEARLY">{t('planManagement.billingInterval.yearly')}</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.displayOrder')}</label>
            <input
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({...formData, displayOrder: parseInt(e.target.value)})}
              className="input-field w-full"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({...formData, active: e.target.checked})}
                className="mr-2"
              />
              <span className="text-gray-400">{t('planManagement.status.active')}</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.popular}
                onChange={(e) => setFormData({...formData, popular: e.target.checked})}
                className="mr-2"
              />
              <span className="text-gray-400">{t('planManagement.popularBadge')}</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.recommended}
                onChange={(e) => setFormData({...formData, recommended: e.target.checked})}
                className="mr-2"
              />
              <span className="text-gray-400">{t('planManagement.recommendedBadge')}</span>
            </label>
          </div>
          <div className="flex space-x-3 pt-4">
            <button type="submit" className="btn-primary flex-1">
              {t('planManagement.actions.create')}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              {t('planManagement.actions.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Plan Modal Component
const EditPlanModal = ({ plan, onSave, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: plan.name,
    code: plan.code,
    description: plan.description || '',
    priceAmount: (plan.priceAmount / 100).toFixed(2),
    currency: plan.currency,
    billingInterval: plan.billingInterval,
    active: plan.active,
    popular: plan.popular,
    recommended: plan.recommended,
    displayOrder: plan.displayOrder
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const planData = {
      ...formData,
      priceAmount: parseInt(parseFloat(formData.priceAmount) * 100) // Convert to cents
    };
    onSave(planData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-white mb-4">{t('planManagement.editModal.title')}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.name')}</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="input-field w-full"
              required
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.code')}</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
              className="input-field w-full"
              required
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="input-field w-full"
              rows="3"
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.price')}</label>
            <input
              type="number"
              step="0.01"
              value={formData.priceAmount}
              onChange={(e) => setFormData({...formData, priceAmount: e.target.value})}
              className="input-field w-full"
              required
            />
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.billingInterval')}</label>
            <select
              value={formData.billingInterval}
              onChange={(e) => setFormData({...formData, billingInterval: e.target.value})}
              className="input-field w-full"
            >
              <option value="MONTHLY">{t('planManagement.billingInterval.monthly')}</option>
              <option value="YEARLY">{t('planManagement.billingInterval.yearly')}</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-400 text-sm mb-2">{t('planManagement.labels.displayOrder')}</label>
            <input
              type="number"
              value={formData.displayOrder}
              onChange={(e) => setFormData({...formData, displayOrder: parseInt(e.target.value)})}
              className="input-field w-full"
            />
          </div>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.active}
                onChange={(e) => setFormData({...formData, active: e.target.checked})}
                className="mr-2"
              />
              <span className="text-gray-400">{t('planManagement.status.active')}</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.popular}
                onChange={(e) => setFormData({...formData, popular: e.target.checked})}
                className="mr-2"
              />
              <span className="text-gray-400">{t('planManagement.popularBadge')}</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.recommended}
                onChange={(e) => setFormData({...formData, recommended: e.target.checked})}
                className="mr-2"
              />
              <span className="text-gray-400">{t('planManagement.recommendedBadge')}</span>
            </label>
          </div>
          <div className="flex space-x-3 pt-4">
            <button type="submit" className="btn-primary flex-1">
              {t('planManagement.actions.saveChanges')}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">
              {t('planManagement.actions.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
const DeletePlanModal = ({ plan, onConfirm, onClose }) => {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-bold text-white mb-4">{t('planManagement.deleteModal.title')}</h3>
        <p className="text-gray-400 mb-6" dangerouslySetInnerHTML={{ __html: t('planManagement.deleteModal.message', { name: plan.name }) }} />
        <div className="flex space-x-3">
          <button onClick={onConfirm} className="btn-danger flex-1">
            {t('planManagement.deleteModal.confirm')}
          </button>
          <button onClick={onClose} className="btn-secondary flex-1">
            {t('planManagement.actions.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanManagement;
